import { ClbIcon } from '@celebration/react';
import { useTranslation } from 'react-i18next';
import { getFormattedDescription } from './utils/load-card-body-utils';
import LoadsConstants from '../../../../pages/business/loads/constants';
import { ReactComponent as MapPinIcon } from '../../../assets/svg/load-card/map-pin.svg';
import { ILoadCardBody } from './ILoadCardBody';
import LoadCardBodyWinner from './components/load-card-body-winner/load-card-body-winner';
import LoadCardBodyReason from './components/load-card-body-reason/load-card-body-reason';
import LoadType from '../../../enums/loads/load-type.enum';
import './load-card-body.scss';

import {
    EMPTY_FIELD,
    getCodeAndDescriptionFormatted,
    getCityAndfederativeUnitDescriptionFormatted,
} from '../../../../utils/string-utils';

export default function LoadCardBody(props: Readonly<ILoadCardBody>) {
    const {
        sourceRoute,
        destinationRoute,
        collectionDate,
        channel,
        loadType,
        showReason,
        reasonMessage,
        winner,
        price,
        weight,
        hasRate = true,
        skids,
        isReturnable,
        isReprogrammed = false,
    } = props;

    const { t } = useTranslation();

    const sourceLabel = getCodeAndDescriptionFormatted(
        sourceRoute.code,

        sourceRoute.description,

        LoadsConstants.NO_INFO_TEXT,
    );

    const sourceLocationLabel = getCityAndfederativeUnitDescriptionFormatted(
        sourceRoute.city,

        sourceRoute.federativeUnit,
    );

    const destinationLabel = getCodeAndDescriptionFormatted(
        destinationRoute.code,

        destinationRoute.description,

        LoadsConstants.NO_INFO_TEXT,
    );

    const destinationLocationLabel =
        getCityAndfederativeUnitDescriptionFormatted(
            destinationRoute.city,

            destinationRoute.federativeUnit,
        );

    const getLoadChannelText = (): string => {
        if (channel) {
            return t(channel);
        }

        return LoadsConstants.NO_INFO_TEXT;
    };

    const renderSuspensionOrWinnerInfo = (): JSX.Element | undefined => {
        if (showReason) {
            return (
                <LoadCardBodyReason
                    showReason={showReason}
                    reasonMessage={reasonMessage ?? EMPTY_FIELD}
                />
            );
        }

        return winner && <LoadCardBodyWinner {...winner} />;
    };

    return (
        <div className="card-loads-body">
            <div className="card-loads-body-top">
                <div className="card-loads-body-route card-loads-body__origin">
                    <ClbIcon icon="location" size="sm" />

                    <span className="card-loads-body__routeDetailLabel">
                        {t('Origem')}
                    </span>

                    <span className="card-loads-body__routeDetailLabel-data">
                        {getFormattedDescription(sourceLabel)}
                    </span>

                    <div className="card-loads-body__routeDetailLabel-data-location">
                        {getFormattedDescription(sourceLocationLabel)}
                    </div>
                </div>

                <div className="card-loads-body-route card-loads-body__destination">
                    <span className="card-loads-body__destination-icon">
                        <MapPinIcon />
                    </span>

                    <span className="card-loads-body__routeDetailLabel">
                        {t('Destino')}
                    </span>

                    <span className="card-loads-body__routeDetailLabel-data">
                        {getFormattedDescription(destinationLabel)}
                    </span>

                    <div className="card-loads-body__routeDetailLabel-data-location">
                        {getFormattedDescription(destinationLocationLabel)}
                    </div>
                </div>

                <div className="card-loads-body__collection-date">
                    <ClbIcon icon="calendar" size="sm" />

                    <span className="card-loads-body__routeDetailLabel">
                        {t('Carregamento')}
                    </span>

                    <span className="card-loads-body__routeDetailLabel-data">
                        {collectionDate &&
                            new Date(collectionDate)?.toLocaleDateString()}
                    </span>
                </div>
            </div>

            <div className="card-loads-body-bottom">
                <div className="load-card-body__attributes">
                    <div className="load-card-body__channel">
                        {getLoadChannelText()}
                    </div>

                    {loadType === LoadType.Dedicated && (
                        <div
                            className="load-card-body__dedicated"
                            data-testid="load-card-body__dedicated"
                        >
                            {t('Dedicated')}
                        </div>
                    )}

                    {loadType === LoadType.SemiDedicated && (
                        <div
                            className="load-card-body__semidedicated"
                            data-testid="load-card-body__semidedicated"
                        >
                            {t('SemiDedicated')}
                        </div>
                    )}

                    {loadType === LoadType.Loyal && (
                        <div
                            className="load-card-body__loyal"
                            data-testid="load-card-body__loyal"
                        >
                            {t('Loyal')}
                        </div>
                    )}

                    {loadType === LoadType.OtherModes && (
                        <div
                            className="load-card-body__othermodes"
                            data-testid="load-card-body__othermodes"
                        >
                            {t('OtherModes')}
                        </div>
                    )}

                    {loadType === LoadType.Hub && (
                        <div
                            className="load-card-body__hub"
                            data-testid="load-card-body__hub"
                        >
                            {t('Hub')}
                        </div>
                    )}

                    {loadType === LoadType.DraftBeer && (
                        <div
                            className="load-card-body__draftbeer"
                            data-testid="load-card-body__draftbeer"
                        >
                            {t('DraftBeer')}
                        </div>
                    )}

                    {weight && (
                        <div className="load-card-body__weight">{weight}</div>
                    )}

                    {price && hasRate && (
                        <div
                            data-testid="load-card-body__price"
                            className="load-card-body__price"
                            style={{
                                visibility: hasRate ? 'visible' : 'hidden',
                            }}
                        >
                            {price}
                        </div>
                    )}

                    {skids != null && Number(skids) > 0 && (
                        <div
                            className="load-card-body__skids"
                            data-testid="load-card-body__skids"
                        >
                            {skids} Plts
                        </div>
                    )}

                    {isReturnable && (
                        <div
                            className="load-card-body__returnable"
                            data-testid="load-card-body__returnable"
                        >
                            {t('Returnable')}
                        </div>
                    )}

                    {isReprogrammed && (
                        <div
                            className="load-card-body__reprogrammed"
                            data-testid="load-card-body__reprogrammed"
                        >
                            {t('Reprogrammed')}
                        </div>
                    )}
                </div>

                {renderSuspensionOrWinnerInfo()}
            </div>
        </div>
    );
}
